import moment from '@utils/moment-wrapper';
import kebabCase from 'lodash/kebabCase';
import isEmpty from 'lodash/isEmpty';
import { AppsService } from '@/common/apps/apps.service';
import { isDefaultSegment, getSegmentName } from '@/utils/segments.js';
import { getGroupId, pickGroupValues } from '@/data-explorer/helpers/common.util';
import { getValidPeriodsForDateRange } from '@/utils/date-range.utils';
import { EngageTimeSeries } from '@/types/engage.types';
import { AUTOMATIC_GROUP_PARAMETERS } from '@/constants/utm-parameters';
import isEqual from 'lodash/isEqual';
import { formatNumberWithSuffix, prettyTime } from '@/utils/formatters';
import { i18n_t as $t } from '@utils/i18n';

export const SESSION_METRICS = ['sessions', 'avgSessionTime'];

export const CONVERSION_METRICS = ['conversions', 'conversionRate'];

export const TIME_METRICS = ['avgSessionTime', 'avgTime'];

export const PERCENTAGE_METRICS = ['conversionRate'];

export const AUTOMATIC_GROUPS = AUTOMATIC_GROUP_PARAMETERS().map((group) => group.field);

export const CLICKABLE_METRIC_TYPES = ['conversions', 'sessions', 'traffic', 'newTraffic'];

export const WEB_ANALYTICS_METRIC_KINDS = [
    'conversions',
    'conversionRate',
    'traffic',
    'newTraffic',
    'sessions',
    'avgSessionTime',
    'avgSessionsPerUser',
    'avgTime',
];

export const WEB_ANALYTICS_ANALYZE_BUTTON = {
    label: $t('webAnalytics.keyMetrics.Analyze full user flow'),
    suffixIcon: 'arrow-up-right',
};

export const DRILLDOWN_USER_LIST_LIMIT = 500;

export const VALID_SESSION_DATE_IN_MS = 1744934400000;

export const VALID_UTM_DATE_IN_MS = 1746057600000;

export const VALID_DATE_FOR_ALL_IN_MS = 1751256000000;

// May 1, 2025
export const VALID_UTM_DATE_STRING = () =>
    moment(VALID_UTM_DATE_IN_MS, { ignoreTimeZone: true })
        .add(1, 'days')
        .format('MMMM YYYY');

// June 30, 2025
export const VALID_DATE_FOR_ALL_STRING = () =>
    moment(VALID_DATE_FOR_ALL_IN_MS, { ignoreTimeZone: true }).format('MMMM YYYY');

export const GROUPING_LIMIT = 5;

export function getSessionDateText (title: string) {
    return `${title} tracking was updated in ${VALID_DATE_FOR_ALL_STRING()}. Earlier data is available but may be less complete for some users.`;
}

export function getWebAnalyticsDownloadFileName ({
    period,
    kind,
    selectedTime,
}: {
    period: string;
    kind: string;
    selectedTime?: string;
}) {
    const date = selectedTime ? moment(selectedTime).format('MM-DD-YYYY') : moment().format('MM-DD-YYYY');

    return `web-analytics-${kebabCase(period)}_${kebabCase(kind)}_${date}`;
}

function conversionAppNameLabel (conversionEvent: Record<string, unknown>) {
    if (!conversionEvent) {
        return '';
    }

    const { appDisplayName } = conversionEvent;
    if (!appDisplayName) {
        return '';
    }

    return ` (${appDisplayName})`;
}

export function getSelectedAppDisplayName (selectedApp: Record<string, unknown> = {}) {
    const { id } = selectedApp;
    if (!id) return '';

    return AppsService.getAppDisplayNameFromId(id);
}

function conversionEventNameLabel (conversionEvent: Record<string, unknown>) {
    return conversionEvent.displayName || conversionEvent.name;
}

export function buildConversionEventLabel (conversionEvent: Record<string, unknown> | undefined) {
    if (isEmpty(conversionEvent)) {
        return '';
    }
    const nameLabel = conversionEventNameLabel(conversionEvent);
    const appLabel = conversionAppNameLabel(conversionEvent);

    return `${nameLabel}${appLabel}`;
}

export function buildConversionRateAppMismatchLabel (conversionEvent: Record<string, unknown> = {}, selectedApp: Record<string, unknown> = {}) {
    const { appDisplayName: conversionAppName } = conversionEvent;
    const selectedAppName = AppsService.getAppDisplayNameFromId(selectedApp?.id);

    if (!conversionAppName || !selectedAppName) return '';

    return $t('webAnalytics.keyMetrics.conversionRateTooltip', {
        conversionAppName,
        selectedAppName,
        openStrongTag: '<strong>',
        closeStrongTag: '</strong>',
        newline: '<br><br>',
    });
}

export function getSessionDateTooltipConfig () {
    return {
        iconType: 'info',
        iconSize: 12,
        content: getSessionDateText('Sessions'),
    };
}

export function buildConversionMetricsSubLabel ({
    conversionEvent,
    selectedApp,
    includeTraffic = false,
}: {
    conversionEvent?: Record<string, unknown>;
    selectedApp?: Record<string, unknown>;
    includeTraffic?: boolean;
}) {
    const conversionFragment = !isEmpty(conversionEvent)
        ? `for ${buildConversionEventLabel(conversionEvent)}`
        : 'who converted from a selected event';

    if (!includeTraffic || !selectedApp) return conversionFragment;

    const appName = AppsService.getAppDisplayNameFromId(selectedApp?.id);

    return `${conversionFragment} and visitors in ${appName}`;
}

export function getConversionRateAppMismatchTooltipConfig ({
    conversionEvent,
    selectedApp,
}: {
    conversionEvent: Record<string, unknown>;
    selectedApp: Record<string, unknown>;
}) {
    const content = buildConversionRateAppMismatchLabel(conversionEvent, selectedApp);
    if (!content) return {};

    return {
        content,
        iconType: 'alert-triangle',
        iconSize: 16,
        iconColor: '#DBA211',
    };
}

type TooltipConfig =
    | {
          content?: string;
          [key: string]: unknown;
      }
    | null
    | undefined;

export function sanitizeTooltipConfig (config: TooltipConfig, sanitizeFn: (content: string) => string): TooltipConfig {
    if (!config || !config.content) {
        return config;
    }

    return {
        ...config,
        content: sanitizeFn(config.content),
    };
}
declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pendo: any;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function sendWebAnalyticsTrackEvent (config: any) {
    const {
        conversionEvent,
        groupings = [],
        appliedFilters = [],
        dateRange,
        segmentId,
        app,
        aggResponses,
        hasHistoricalMetadata,
        hasNonHistoricalMetadata,
        hasDeviceProps,
        hasUtmParameters,
        hasAutomaticGroups,
        groupByUTMParameters,
        groupByAutomaticGroups,
    } = config;

    let rowCount = 0;
    if (groupings.length < 1) {
        rowCount = Object.values(aggResponses).length;
    }

    const segment = (await isDefaultSegment(segmentId)) ? 'default' : 'custom';
    const segmentName = await getSegmentName(segmentId);

    const trackEventProperties = {
        conversionEventName: conversionEvent?.name,
        conversionEventType: conversionEvent?.kind,
        hasGroups: groupings.length > 0,
        numberOfGroups: groupings.length,
        hasFilters: appliedFilters.length > 0,
        numberOfFilters: appliedFilters.length,
        dateRange: dateRange.config.type,
        dateRangeDays: dateRange.spec.count,
        app: app.displayName,
        segment,
        segmentName,
        rowCount,
        hasHistoricalMetadata,
        hasNonHistoricalMetadata,
        hasDeviceProps,
        hasAutomaticGroups,
        hasUtmParameters,
        groupByUTMParameters,
        groupByAutomaticGroups,
    };

    window.pendo.track('Web Analytics', trackEventProperties);
}

function addPointToSeries (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    series: Record<string, any[]>,
    timestamp: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    point: Record<string, any> = {},
): void {
    const normalizedPoint = {
        accounts: [],
        visitors: [],
        totalVisitors: 0,
        totalAccounts: 0,
        ...point,
        timestamp: parseInt(timestamp),
    };

    const existingItem = series.data.findIndex((item) => item.timestamp === normalizedPoint.timestamp);
    if (existingItem !== -1) {
        series.data[existingItem] = normalizedPoint;
    } else {
        series.data.push(normalizedPoint);
    }
}

function createBackfilledSeriesData (
    periods: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    groupValues: Record<string, any> = {},
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const seriesData: Record<string, any[]> = {
        data: [],
    };

    for (const period of periods) {
        addPointToSeries(seriesData, period, groupValues);
    }

    return seriesData;
}

interface AggData {
    periods?: string[] | number[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any[];
}

function createSeriesDataFromRaw (aggData: AggData, possiblePeriods: Array<number>) {
    const { data } = aggData;
    const periodCopy = JSON.parse(JSON.stringify(possiblePeriods));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataById: Record<string, any> = {};

    data.forEach(([timestamp, dataArray = []]) => {
        const seriesIdsToBackfill = new Set<string>(Object.keys(dataById));

        for (const point of dataArray) {
            const id = getGroupId(point);

            let matchingSeries = dataById[id];
            if (!matchingSeries) {
                const pickedGroupValues = pickGroupValues(point);
                matchingSeries = createBackfilledSeriesData(periodCopy.slice(), pickedGroupValues);
                Object.assign(matchingSeries, { ...pickedGroupValues });
                dataById[id] = matchingSeries;
            }

            addPointToSeries(matchingSeries, timestamp, point);
            seriesIdsToBackfill.delete(id);
        }
    });

    return dataById;
}

export function processAggResponse (aggData: AggData, possiblePeriods: Array<number>) {
    return createSeriesDataFromRaw(aggData, possiblePeriods);
}

export function isValidPeriodForNewTrafficMetric (timeSeries: EngageTimeSeries) {
    const validPeriods = getValidPeriodsForDateRange(timeSeries);
    const timeSeriesCount = timeSeries?.spec?.count;

    return !(validPeriods.includes('hourly') && timeSeriesCount === 1);
}

export function extractGroupValues (group: Record<string, unknown>) {
    return Object.fromEntries(Object.entries(group).filter(([key]) => key.startsWith('groupValue')));
}

export function getMatchingDataGroup (groupValues: Record<string, unknown>, data: Array<Record<string, unknown>> = []) {
    return data?.find((group) => isEqual(groupValues, extractGroupValues(group)));
}

export function calculateRatio (numerator: number, denominator: number) {
    if (denominator === 0) return 0;

    return numerator / denominator;
}

export function buildSubgroupsMap (groupedDataToMap: Array<Record<string, unknown>> = [], timeInterval: string) {
    return (groupedDataToMap || [])
        .flatMap((group) => (Array.isArray(group.subgroup) ? group.subgroup : []))
        .reduce((map: Record<string, unknown[]>, subgroup: Record<string, unknown>) => {
            const subgroupTimeKey = String(subgroup?.[timeInterval]);
            if (!map[subgroupTimeKey]) {
                map[subgroupTimeKey] = [];
            }

            map[subgroupTimeKey].push(subgroup);

            return map;
        }, {});
}

export const AGG_DATA_STREAM_NAMES = {
    CONVERSIONS: 'conversions',
    SHARED: 'shared',
    TIME: 'time',
    NEW_TRAFFIC: 'newTraffic',
} as const;

export const METRICS_CONFIGS = {
    conversions: {
        dataStreams: [AGG_DATA_STREAM_NAMES.CONVERSIONS],
        formatter: formatNumberWithSuffix,
        value: {
            name: 'conversions',
            dataStream: AGG_DATA_STREAM_NAMES.CONVERSIONS,
        },
        previousValue: {
            name: 'prevConversions',
            dataStream: AGG_DATA_STREAM_NAMES.CONVERSIONS,
        },
    },
    conversionRate: {
        dataStreams: [AGG_DATA_STREAM_NAMES.CONVERSIONS, AGG_DATA_STREAM_NAMES.SHARED],
        formatter (value: number) {
            return formatNumberWithSuffix(value, { style: 'percent', percentFormat: 'long' });
        },
        value: {
            name: 'conversionRate',
            ratioValues: {
                numerator: {
                    name: 'conversions',
                    dataStream: AGG_DATA_STREAM_NAMES.CONVERSIONS,
                },
                denominator: {
                    name: 'traffic',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
            },
        },
        previousValue: {
            name: 'previousConversionRate',
            ratioValues: {
                numerator: {
                    name: 'prevConversions',
                    dataStream: AGG_DATA_STREAM_NAMES.CONVERSIONS,
                },
                denominator: {
                    name: 'previousTraffic',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
            },
        },
    },
    traffic: {
        dataStreams: [AGG_DATA_STREAM_NAMES.SHARED],
        formatter: formatNumberWithSuffix,
        value: {
            name: 'traffic',
            dataStream: AGG_DATA_STREAM_NAMES.SHARED,
        },
        previousValue: {
            name: 'previousTraffic',
            dataStream: AGG_DATA_STREAM_NAMES.SHARED,
        },
    },
    newTraffic: {
        dataStreams: [AGG_DATA_STREAM_NAMES.NEW_TRAFFIC],
        formatter: formatNumberWithSuffix,
        value: {
            name: 'newTraffic',
            dataStream: AGG_DATA_STREAM_NAMES.NEW_TRAFFIC,
        },
        previousValue: {
            name: 'prevNewTraffic',
            dataStream: AGG_DATA_STREAM_NAMES.NEW_TRAFFIC,
        },
    },
    sessions: {
        dataStreams: [AGG_DATA_STREAM_NAMES.SHARED],
        formatter: formatNumberWithSuffix,
        value: {
            name: 'sessions',
            dataStream: AGG_DATA_STREAM_NAMES.SHARED,
        },
        previousValue: {
            name: 'previousSessions',
            dataStream: AGG_DATA_STREAM_NAMES.SHARED,
        },
    },
    avgTime: {
        dataStreams: [AGG_DATA_STREAM_NAMES.SHARED, AGG_DATA_STREAM_NAMES.TIME],
        formatter: prettyTime,
        value: {
            name: 'avgTime',
            ratioValues: {
                numerator: {
                    name: 'totalTimeInMS',
                    dataStream: AGG_DATA_STREAM_NAMES.TIME,
                },
                denominator: {
                    name: 'traffic',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
            },
        },
        previousValue: {
            name: 'previousAvgTime',
            ratioValues: {
                numerator: {
                    name: 'previousTotalTimeInMS',
                    dataStream: AGG_DATA_STREAM_NAMES.TIME,
                },
                denominator: {
                    name: 'previousTraffic',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
            },
        },
    },
    avgSessionsPerUser: {
        dataStreams: [AGG_DATA_STREAM_NAMES.SHARED],
        formatter (value: number) {
            return formatNumberWithSuffix(value, { maxFractionDigits: 1 });
        },
        value: {
            name: 'avgSessionsPerUser',
            dataStream: AGG_DATA_STREAM_NAMES.SHARED,
            ratioValues: {
                numerator: {
                    name: 'sessions',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
                denominator: {
                    name: 'traffic',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
            },
        },
        previousValue: {
            name: 'previousAvgSessionsPerUser',
            dataStream: AGG_DATA_STREAM_NAMES.SHARED,
            ratioValues: {
                numerator: {
                    name: 'previousSessions',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
                denominator: {
                    name: 'previousTraffic',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
            },
        },
    },
    avgSessionTime: {
        dataStreams: [AGG_DATA_STREAM_NAMES.SHARED, AGG_DATA_STREAM_NAMES.TIME],
        formatter: prettyTime,
        value: {
            name: 'avgSessionTime',
            ratioValues: {
                numerator: {
                    name: 'totalTimeInMS',
                    dataStream: AGG_DATA_STREAM_NAMES.TIME,
                },
                denominator: {
                    name: 'sessions',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
            },
        },
        previousValue: {
            name: 'previousAvgSessionTime',
            ratioValues: {
                numerator: {
                    name: 'previousTotalTimeInMS',
                    dataStream: AGG_DATA_STREAM_NAMES.TIME,
                },
                denominator: {
                    name: 'previousSessions',
                    dataStream: AGG_DATA_STREAM_NAMES.SHARED,
                },
            },
        },
    },
} as const;

export function processMetricValues ({
    kind,
    data,
    includePrevious,
    additionalValues = {},
}: {
    kind: string;
    data: Record<string, unknown>;
    includePrevious: boolean;
    additionalValues: Record<string, unknown>;
}) {
    const metricConfig = METRICS_CONFIGS[kind as keyof typeof METRICS_CONFIGS];
    if (!metricConfig) return {};
    const keys = ['value'];
    if (includePrevious) keys.push('previousValue');
    const values = keys.reduce((acc: Record<string, unknown>, key) => {
        const configValue = metricConfig[key as keyof typeof metricConfig] as Record<string, unknown>;
        const { name, dataStream, ratioValues } = configValue;

        if (ratioValues) {
            const ratioConfig = ratioValues as {
                numerator: { dataStream: string; name: string };
                denominator: { dataStream: string; name: string };
            };
            const numerator = Number(
                (data[ratioConfig.numerator.dataStream] as Record<string, unknown>)[ratioConfig.numerator.name] || 0,
            );
            const denominator = Number(
                (data[ratioConfig.denominator.dataStream] as Record<string, unknown>)[ratioConfig.denominator.name] ||
                    0,
            );
            acc[name as string] = calculateRatio(numerator, denominator);
        } else {
            acc[name as string] = (data[dataStream as string] as Record<string, unknown>)?.[name as string];
        }

        return acc;
    }, {});

    return { ...values, ...additionalValues };
}
